package org.multiplatform.kotfolio.pages.coroutines

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("/coroutines/filter")
@Composable
fun FilterPage() {
    BlogLayout(
        PageProperties(
            "Coroutines Filter",
            ""),
        BlogProperties("Coroutines", "Combine | Zip | Filter")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "combine: combine is the backbone of modern ViewModels because Ul is snapshot-driven. A screen needs a \"full picture\" of the search query, selected filters, and data all at once. Can trigger expensive downstream work if upstream flows emit too frequently. Use distinctUntilChanged() or debounce() to protect the UI layer.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "zip(): Dangerous for high-frequency streams. It may buffer values aggressively, leading to memory overhead if emission rates differ significantly.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "merge: merge performs no semantic coordination between emissions. It treats every incoming value as an independent event. It acts as a funnel, pouring multiple streams into one.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "zip(): The Positional Matchmaker: zip is a coordinated, 1-to-1 operator. It synchronizes emissions purely based on their position: the n-th emission of Flow A matches the n-th of Flow B.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                    fun search(term: String) = flow {
                        for (book in books) {
                            delay(250.milliseconds) // simulate a search delay
                            if (book.title.contains(term, ignoreCase = true) ||
                                book.author.contains(term, ignoreCase = true)
                            ) {
                                emit(book)
                            }
                        }
                    }

                    fun main() = runBlocking {
                        search("Mary Shelley")
                            .onStart { println("\nStarting search..") }
                            .onCompletion { println("\nDone searching.") }
                            .onEmpty { println("No results found.") }
                            .collect { println("Found matching book: ${it.title} by ${it.author}") }
                    }

                    data class Book(
                        val id: String,
                        val title: String,
                        val author: String
                    )

                    private val books = listOf(
                        Book("1661", "The Adventures of Sherlock Holmes", "Arthur Conan Doyle"),
                        Book("1342", "Pride and Prejudice", "Jane Austen"),
                        Book("1400", "Great Expectations", "Charles Dickens"),
                        Book("41445", "Frankenstein", "Mary Shelley"),
                        Book("100", "Complete Works", "William Shakespeare"),
                        Book("17157", "Gulliver's Travels", "Jonathan Swift"),
                        Book("11", "Alice's Adventures in Wonderland", "Lewis Carroll"),
                        Book("74", "The Adventures of Tom Sawyer", "Mark Twain"),
                        Book("35", "The Time Machine", "H. G. Wells"),
                        Book("103", "Around the World in Eighty Days", "Jules Verne")
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    //while inside flow{} creates values, while sample() limits how often downstream receives values.
                    
                    val currencyFormat: NumberFormat =
                        NumberFormat.getCurrencyInstance(Locale.US)

                    fun stockTicker(startPrice: Int) = flow {
                        var price = startPrice
                        while (true) {
                            delay(500)
                            val delta = Random.nextInt(-100, 100)
                            price = (price + delta).coerceAtLeast(1)
                            emit(price)
                        }
                    }

                    @OptIn(FlowPreview::class)
                    fun main() = runBlocking {
                        stockTicker(150_00)
                            .sample(3.seconds)
                            .map { currencyFormat.format(it / 100.0) }
                            .take(100)
                            .collect { println("\n$it") }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                    fun main() = runBlocking {
                        flowOf(
                            "2025-06-05",
                            "2025-06-06",
                            "2025-06-07",
                            "2025-06-08",
                            "2025-06-09"
                        )
                            //.transform { emit(LocalDate.parse(it)) }
                            .transform {
                                emit("\n$it\n................")
                                eventsFor(it).forEach { event -> emit(event) }
                            }
                            //.filter { it.dayOfWeek !in setOf(DayOfWeek.SATURDAY, DayOfWeek.SUNDAY) }
                            //.take(5)
                            .collect { println(it) }
                    }
                    
                    fun main() = runBlocking {
                        flowOf(
                            "2025-06-05",
                            "2025-06-06",
                            "2025-06-07",
                            "2025-06-08",
                            "2025-06-09"
                        )
                            .map { LocalDate.parse(it) } // convert to LocalDate
                            .filter { it.dayOfWeek !in setOf(DayOfWeek.SATURDAY, DayOfWeek.SUNDAY) } // remove weekends
                            .map { it.toString() } // convert back to String
                            .transform {
                                emit("\n$it\n................")
                                eventsFor(it).forEach { event -> emit(event) }
                            }
                            .collect { println(it) }

                    }
                    
                    fun eventsFor(date: String): List<String> = when (date) {
                        "2025-06-04" -> listOf("Stand-up", "Interview Candidate")
                        "2025-06-05" -> listOf("Stand-up", "Sync-up with Jon")
                        "2025-06-06" -> listOf("Stand-up", "Product Demos")
                        "2025-06-07" -> listOf("Coffee at Jim's")
                        "2025-06-08" -> listOf("Family Luncheon")
                        "2025-06-09" -> listOf("Staff meeting", "Lunch with Libby", "Design Meeting")
                        "2025-06-10" -> listOf("Stand-up", "Haircut at Bert's")
                        "2025-06-11" -> listOf("Stand-up")
                        "2025-06-12" -> listOf("Stand-up", "Call with Vendor")
                        "2025-06-13" -> listOf("Stand-up")
                        else -> emptyList()
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Latest way filter",
                modifier = Modifier.fontSize(FontSize.Large).padding(bottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    // Chaining multiple operations with asSequence
                    val result = listOf(1, 2, 3, 4, 5)
                        .asSequence() //Most Important
                        .map { it * 2 }  // Multiply each element by 2
                        .filter { it > 5 }  // Filter out elements less than or equal to 5
                        .take(2)  // Take the first 2 elements
                        .toList()  // Convert the sequence back to a list
                    println(result)  // Output: [6, 8]
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                     fun main() {
                         // Who sent the most messages?
                         val frequentSender = messages
                             .groupBy(Message::sender)
                             .maxByOrNull { (_, messages) -> messages.size }
                             ?.key                                                 // Get their names
                         println(frequentSender) // [Ma]

                         // Who are the senders?
                         val senders = messages
                             .asSequence()                                         // Make operations lazy (for a long call chain)
                             .filter { it.body.isNotBlank() && !it.isRead }        // Use lambdas...
                             .map(Message::sender)                                 // ...or member references
                             .distinct()
                             .sorted()
                             .toList()                                             // Convert sequence back to a list to get a result
                         println(senders) // [Adam, Ma]
                     }

                     data class Message(                                           // Create a data class
                         val sender: String,
                         val body: String,
                         val isRead: Boolean = false,                              // Provide a default value for the argument
                     )

                     val messages = listOf(                                        // Create a list
                         Message("Ma", "Hey! Where are you?"),
                         Message("Adam", "Everything going according to plan today?"),
                         Message("Ma", "Please reply. I've lost you!"),
                     )
                """.trimIndent()
            )


            KotlinCode(
                code = """
                    internal class MovieSearchVM() : ViewModel() {
                        private val _queryState = MutableStateFlow("")
                        val queryState get() = _queryState.asStateFlow()

                        @OptIn(ExperimentalCoroutinesApi::class, FlowPreview::class)
                        val movies: Flow<PagingData<Movie>> = queryState
                            .filter { it.isNotBlank() }
                            .debounce(1000)
                            .distinctUntilChanged()
                            .flatMapLatest { query ->
                               
                            }
                            .cachedIn(viewModelScope)

                        fun onQueryChange(query: String) {
                            _queryState.update { query }
                        }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    sealed class PlayerListUIState {
                        object Loading : PlayerListUIState()
                        data class Error(val message: String) : PlayerListUIState()
                        data class Success(val result: List<Player>) : PlayerListUIState()
                    }

                    @OptIn(FlowPreview::class, ExperimentalCoroutinesApi::class)
                    open class PlayerListViewModel : ViewModel(), KoinComponent {
                        private val repository: FantasyPremierLeagueRepository by inject()

                        val allPlayers = repository.playerList
                        
                        val searchQuery = MutableStateFlow("")
                        
                        val playerListUIState: StateFlow<PlayerListUIState> =
                            searchQuery.debounce(250).flatMapLatest { searchQuery ->
                                allPlayers.mapLatest { playerList ->
                                    if (playerList.isNotEmpty()) {
                                        val players = playerList
                                            .filter { it.name.contains(searchQuery, ignoreCase = true) }
                                            .sortedByDescending { it.points }
                                        PlayerListUIState.Success(players)
                                    } else {
                                        PlayerListUIState.Loading
                                    }
                                }
                            }.stateIn(viewModelScope, SharingStarted.Eagerly, PlayerListUIState.Loading)

                        fun onPlayerSearchQueryChange(query: String) {
                            searchQuery.value = query
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @Composable
                    fun PlayerListView(
                        onPlayerSelected: (playerId: Int) -> Unit,
                        onShowSettings: () -> Unit
                    ) {
                        val playerListViewModel = koinViewModel<PlayerListViewModel>()
                        val playerListUIState = playerListViewModel.playerListUIState.collectAsStateWithLifecycle()

                        val allPlayers = playerListViewModel.allPlayers.collectAsStateWithLifecycle()
                        val playerSearchQuery = playerListViewModel.searchQuery.collectAsStateWithLifecycle()

                        when (val uiState = playerListUIState.value) {
                            is PlayerListUIState.Error -> { }
                            PlayerListUIState.Loading -> { }
                            is PlayerListUIState.Success -> { }
                        }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "Filter / Search using Combine",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    class MainViewModel : ViewModel() {
                        private val _searchText = MutableStateFlow("")
                        val searchText = _searchText.asStateFlow()

                       private val _isSearching = MutableStateFlow(false)
                        val isSearching = _isSearching.asStateFlow()

                        private val moviesList =  listOf(
                            Movie(
                                0,
                                "Oppenheimer",
                                "Oppenheimer (2023) is a biographical thriller about the life of J. Robert Oppenheimer, the scientist who led the Manhattan Project to develop the atomic bomb. The film explores the moral and ethical implications of Oppenheimer\\'s work, and the impact it had on his personal life.",
                                R.drawable.oppenheimer,
                                4f,
                                "1.5M",
                                "Cristopher Nolan",
                                listOf("Cylian Murphy", "Cylian Murphy", "Cylian Murphy")
                            )
                        )
                        private val _movies = MutableStateFlow(moviesList)

                        //movies used to LazyRow / LazyColumn
                        val movies = searchText
                            .debounce(500L)
                            .onEach { _isSearching.update { true } }
                            .combine(_movies){text, movies ->
                                if(text.isBlank()){
                                    movies
                                }else{
                                    movies.filter {
                                        delay(500L)
                                        it.doesMatchSearchQuery(text)
                                    }
                                }
                            }.onEach { _isSearching.update { false } }
                            .stateIn(viewModelScope, SharingStarted.WhileSubscribed(5000),_movies.value)

                        fun onSearchTextChanged(text : String){
                            _searchText.value = text
                        }
                    }
                    
                    @Composable
                    fun SearchBar(modifier: Modifier = Modifier, viewModel : MainViewModel){
                        val searchText by viewModel.searchText.collectAsState()
                        val isTrailingIconVisible = searchText.isNotBlank()
                        OutlinedTextField(
                            value = searchText,
                            onValueChange = viewModel::onSearchTextChanged,
                            modifier = modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surfaceColorAtElevation(6.dp)),
                            placeholder = { Text(text = "Search Movies", color = Color.LightGray) },
                            leadingIcon = {
                                Icon(
                                    imageVector = Icons.Default.Search,
                                    contentDescription = stringResource(R.string.search),
                                    tint = Color.LightGray
                                )
                            },
                            trailingIcon = {
                                if (isTrailingIconVisible)
                                    Icon(
                                        modifier = Modifier.clickable { viewModel.onSearchTextChanged("") },
                                        imageVector = Icons.Default.Close,
                                        contentDescription = stringResource(R.string.clear_search_text_icon),
                                        tint = Color.LightGray
                                    )
                            },
                            shape = RoundedCornerShape(10.dp),
                        )
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
private val currentSearchQuery = MutableStateFlow("")
private val isLoadingAutofillSuggestions = MutableStateFlow(false)
private val isLoadingSavedLocations = MutableStateFlow(false)
// saved locations are fetched from the local database
private val weatherDetailsOfSavedLocations = weatherRepository.getSavedLocationsListStream()
// whenever the current search query changes, this flow will fetch the suggested places 
// for that query.
private val autofillSuggestions = currentSearchQuery
    .filterNotNull()
    .distinctUntilChanged()
    .debounce(300)
    .filter { it.isNotBlank() }
    .mapLatest { query ->
        isLoadingAutofillSuggestions.value = true
        locationServicesRepository.fetchSuggestedPlacesForQuery(query)
            .also { isLoadingAutofillSuggestions.value = false }
    }

val uiState = combine(
    isLoadingSavedLocations, // state flow
    isLoadingAutofillSuggestions, // state flow
    weatherDetailsOfSavedLocations, // flow
    autofillSuggestions // flow
) { isLoadingSavedLocations, isLoadingAutofillSuggestions, weatherDetailsOfSavedLocations, autofillSuggestions ->
    HomeScreenUiState(.....)
}.stateIn(
    scope = viewModelScope,
    started = SharingStarted.WhileSubscribed(300),
    initialValue = HomeScreenUiState(isLoadingSavedLocations = true)
)
""".trimIndent()
            )

            SpanText(
                text = "Combine using single entity result store",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    @HiltViewModel
                    class TaskViewModel @Inject constructor(
                        private val taskRepository: TaskRepository ,
                        private val subjectRepository: SubjectRepository ,
                        savedStateHandle: SavedStateHandle
                    ) : ViewModel() {

                        private val navArgs: TaskScreenNavArgs = savedStateHandle.navArgs()

                        private val _state = MutableStateFlow(TaskState())
                        
                        val state = combine(
                            _state,
                            subjectRepository.getAllSubjects()
                        ) { state, subjects ->
                            state.copy(subjects = subjects)
                        }.stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(stopTimeoutMillis = 5000),
                            initialValue = TaskState()
                        )

                        init {
                            fetchTask()
                            fetchSubject()
                        }

                    private fun fetchTask() {
                            viewModelScope.launch {
                                navArgs.taskId?.let { id ->
                                    taskRepository.getTaskById(id)?.let { task ->
                                        _state.update {
                                            it.copy(
                                                title = task.title,
                                                description = task.description,
                                                dueDate = task.dueDate,
                                                isTaskComplete = task.isComplete,
                                                relatedToSubject = task.relatedToSubject,
                                                priority = Priority.fromInt(task.priority),
                                                subjectId = task.taskSubjectId,
                                                currentTaskId = task.taskId
                                            )
                                        }
                                    }
                                }
                            }
                        }

                    private fun fetchSubject() {
                            viewModelScope.launch {
                                navArgs.subjectId?.let { id ->
                                    subjectRepository.getSubjectById(id)?.let { subject ->
                                        _state.update {
                                            it.copy(
                                                subjectId = subject.subjectID,
                                                relatedToSubject = subject.name
                                            )
                                        }
                                    }
                                }
                            }
                        }
                    }

                    data class TaskState(
                        val title: String = "" ,
                        val description: String = "" ,
                        val dueDate: Long? = null ,
                        val isTaskComplete: Boolean = false ,
                        val priority: Priority = Priority.LOW ,
                        val relatedToSubject: String? = null ,
                        val subjects: List<Subject> = emptyList() ,
                        val subjectId: Int? = null ,
                        val currentTaskId: Int? = null
                    )

                    //Access data
                    val viewModel: TaskViewModel = hiltViewModel()
                    val state by viewModel.state.collectAsStateWithLifecycle()
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        //Good
                        var characterList = mutableStateOf<List<CharacterItem>>(listOf())
                        var message = mutableStateOf("")
                        var errorMessage = mutableStateOf("")

                        var searchedList = mutableStateOf<List<CharacterItem>>(listOf())
                        private var isSearchEmpty = true
                        
                        fun searchCharacterList(query: String) {
                            val listToSearch = if (isSearchEmpty) characterList.value else searchedList.value

                            viewModelScope.launch(Dispatchers.Main) {

                                if (query.isEmpty()) {
                                    searchedList.value = emptyList()
                                    message.value = "No Search Results"
                                    isSearchEmpty = true
                                    return@launch
                                }

                                val results = listToSearch.filter {
                                    it.name.contains(query.trim(), ignoreCase = true)
                                }

                                if (errorMessage.value.isEmpty()) {
                                    searchedList.value = results
                                    message.value = ""
                                }
                            }
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    val apps by viewModel.filteredApps.collectAsState(initial = emptyList())

                    private val _searchTerm = MutableStateFlow("")
                    val searchTerm = _searchTerm.asStateFlow()

                       val filteredApps = combine(
                            projects, searchTerm
                        ) { apps, searchTerm ->
                            apps.filterBySearchTerm(searchTerm)
                        }

                    fun List<Project>.filterBySearchTerm(searchTerm: String) : List<Project> {
                        return filter { appInfo ->
                            appInfo.language.lowercase().filterNot { it.isWhitespace() }
                                .contains(searchTerm.lowercase().filterNot { it.isWhitespace() })
                        }.sortedBy { it.language.lowercase() }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    class AllBooksViewModel(
                        repository: BooksRepository
                    ) : ViewModel() {

                        private var searchQuery by mutableStateOf("")

                        private val uiState: Flow<List<Book>> = repository.getAllBooks()
                            .map { it.ifEmpty { listOf(Book.defaultBook) } } // At least the default book


                        val searchResults: StateFlow<List<Book>> = snapshotFlow { searchQuery }
                            .combine(uiState) { searchQuery, uiState ->
                                when {
                                    searchQuery.isNotEmpty() -> uiState.filter {
                                        it.title.contains(searchQuery, false) || it.author.contains(searchQuery, false)
                                    }
                                    else -> uiState
                                }
                            }.stateIn(
                                scope = viewModelScope,
                                started = SharingStarted.WhileSubscribed(5000L),
                                initialValue = listOf()
                            )

                        fun applyFilter(filter: String) {
                            searchQuery = filter
                        }
                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    @OptIn(FlowPreview::class)
                    class HomeViewModel(
                        private val notesRepository: NotesRepository,
                        coroutineScope: CoroutineScope?,
                        defaultDispatcher: CoroutineDispatcher
                    ) {

                        private val viewModelScope = coroutineScope ?: MainScope()

                        /**
                         * The current [HomeScreenUiState]
                         */
                        private val _uiState = MutableStateFlow(HomeScreenUiState(isLoadingSavedNotes = true))
                        val uiState = _uiState.asNativeStateFlow()

                        private val currentSearchText = MutableStateFlow("")
                        private var recentlyDeletedNote: Note? = null

                        init {
                            notesRepository.savedNotesStream.onEach { savedNotesList ->
                                _uiState.update { it.copy(isLoadingSavedNotes = false, savedNotes = savedNotesList) }
                            }.launchIn(viewModelScope)

                            combine(
                                uiState, currentSearchText.debounce(200)
                            ) { updatedUiState, searchText ->
                                val savedNotes = updatedUiState.savedNotes
                                // filtering notes with titles containing the search text
                                _uiState.update { it.copy(isLoadingSearchResults = true) }
                                val notesWithTitleContainingSearchText = savedNotes.filter {
                                    it.title.contains(searchText, ignoreCase = true)
                                }
                                _uiState.update { it.copy(searchResults = notesWithTitleContainingSearchText) }

                                // filtering notes with the content containing the search text.
                                // Since this is slower than the previous filtering operation above,
                                // update ui state independently
                                val notesWithContentContainingSearchText = savedNotes.filter {
                                    it.content.contains(searchText, ignoreCase = true)
                                }
                                _uiState.update {
                                    it.copy(searchResults = (it.searchResults + notesWithContentContainingSearchText).distinct())
                                }
                                _uiState.update {
                                    it.copy(isLoadingSearchResults = false)
                                }
                            }.flowOn(defaultDispatcher).launchIn(viewModelScope)
                        }

                        /**
                         * Searches for notes with titles or contents containing the given [searchText].
                         */
                        fun search(searchText: String) {
                            currentSearchText.value = searchText
                        }

                        fun deleteNote(note: Note) {
                            viewModelScope.launch {
                                notesRepository.deleteNote(note)
                                recentlyDeletedNote = note
                            }
                        }

                        fun restoreRecentlyDeletedNote() {
                            viewModelScope.launch { recentlyDeletedNote?.let { notesRepository.saveNote(it) } }
                        }
                    }


                    /**
                     * The UI state for the home screen.
                     */
                    data class HomeScreenUiState(
                        val isLoadingSavedNotes: Boolean = false,
                        val isLoadingSearchResults: Boolean = false,
                        val savedNotes: List<Note> = emptyList(),
                        val searchResults: List<Note> = emptyList()
                    )
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    data class Note(
                        val id: String,
                        val title: String,
                        val content: String,
                        val createdAtTimestampMillis: Long,
                        val isDeleted: Boolean
                    )
                    
                    interface NotesRepository {

                        /**
                         * A [Flow] of all saved notes.
                         */
                        val savedNotesStream: Flow<List<Note>>

                        /**
                         * Used to save a [Note].
                         */
                        suspend fun saveNote(note: Note)

                        /**
                         * Used to delete a [Note].
                         */
                        suspend fun deleteNote(note: Note)

                        /**
                         * Used to delete all saved [Note]s.
                         */
                        suspend fun deleteAllNotesMarkedAsDeleted()
                    }
                    """.trimIndent()
            )

            //advanceUntilIdle() is a function from the testing tools that lets you skip the wait and fast-forward through time in your test environment. Instead of waiting for real-time delays, this function moves the virtual clock forward until all the coroutines have finished. That way, your test doesn’t have to sit there waiting—it just runs everything instantly.
            KotlinCode(
                code = """
                    @OptIn(ExperimentalCoroutinesApi::class)
                    class HomeViewModelTest {

                        private lateinit var homeViewModel: HomeViewModel
                        private val testScope = TestScope(context = StandardTestDispatcher())
                        private val savedNotes = List(10) { index ->
                            Note(
                                id = index.toString(),
                                title = index,
                                content = index,
                                isDeleted = false,
                                createdAtTimestampMillis = Clock.System.now().epochSeconds * index
                            )
                        }

                        @BeforeTest
                        fun setup() {
                            val notesRepositoryMock = object : NotesRepository {
                                override val savedNotesStream: Flow<List<Note>> = flowOf(savedNotes)

                                override suspend fun saveNote(note: Note) {
                                    TODO("Not yet implemented")
                                }

                                override suspend fun deleteNote(note: Note) {
                                    TODO("Not yet implemented")
                                }

                                override suspend fun deleteAllNotesMarkedAsDeleted() {
                                    TODO("Not yet implemented")
                                }
                            }

                            homeViewModel = HomeViewModel(
                                notesRepository = notesRepositoryMock,
                                coroutineScope = testScope,
                                defaultDispatcher = StandardTestDispatcher()
                            )
                        }

                        @Test
                        fun `searchNotesTest - Notes with titles containing the search text much be fetched successfully`() =
                            testScope.runTest {
                                homeViewModel.search("Note #1")
                                advanceUntilIdle()
                                assertTrue {
                                    val expectedNoteToBeReturned = savedNotes.find { it.title == "Note #1" }
                                    val expectedNoteListToBeReturned = listOf(expectedNoteToBeReturned)
                                    homeViewModel.uiState.value.searchResults.containsAll(expectedNoteListToBeReturned)
                                }

                                homeViewModel.search("Note #2")
                                advanceUntilIdle()
                                assertTrue {
                                    val expectedNoteToBeReturned = savedNotes.find { it.title == "Note #2" }
                                    val expectedNoteListToBeReturned = listOf(expectedNoteToBeReturned)
                                    homeViewModel.uiState.value.searchResults.containsAll(expectedNoteListToBeReturned)
                                }
                            }

                        @Test
                        fun `searchNotesTest - Notes with contents containing the search text much be fetched successfully`() =
                            testScope.runTest {
                                homeViewModel.search("Note #1")
                                advanceUntilIdle()
                                assertTrue {
                                    val expectedNoteToBeReturned = savedNotes.find { it.content == "Note #1" }
                                    val expectedNoteListToBeReturned = listOf(expectedNoteToBeReturned)
                                    homeViewModel.uiState.value.searchResults.containsAll(expectedNoteListToBeReturned)
                                }

                                homeViewModel.search("Note #2")
                                advanceUntilIdle()
                                assertTrue {
                                    val expectedNoteToBeReturned = savedNotes.find { it.content == "Note #2" }
                                    val expectedNoteListToBeReturned = listOf(expectedNoteToBeReturned)
                                    homeViewModel.uiState.value.searchResults.containsAll(expectedNoteListToBeReturned)
                                }
                            }

                    }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                        data class State (
                            val searchQuery: String = ""
                        )
                    
                        @OptIn(FlowPreview::class)
                        private fun observeSearch() {
                            state
                                .map { it.searchQuery }
                                .distinctUntilChanged()
                                .debounce(500L)
                                .onEach { query ->
                                    when {
                                        query.isBlank() -> {
                                            _state.update {
                                               
                                            }
                                        }

                                        query.length >= 2 -> {
                                            searchJob?.cancel()
                                        }
                                    }
                                }
                                .launchIn(viewModelScope)
                        }
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    ❌ The Seemingly Smart Optimization
                    @Composable
                    fun MessageList(messages: List<Message>) {
                        val searchQuery by searchViewModel.query.collectAsState()
                        
                        // "I'll only recompose when filtered results change!"
                        val filteredMessages by remember(messages, searchQuery) {
                            derivedStateOf {
                                messages.filter { 
                                    it.content.contains(searchQuery, ignoreCase = true) 
                                }
                            }
                        }
                        
                        LazyColumn {
                            items(
                                items = filteredMessages,
                                key = { it.id }
                            ) { message ->
                                MessageCard(message)
                            }
                        }
                    }

                    ✅ The Correct Pattern: ViewModel-Level State
                    class MessageViewModel : ViewModel() {
                        private val _searchQuery = MutableStateFlow("")
                        private val _messages = MutableStateFlow<List<Message>>(emptyList())
                        
                        val filteredMessages: StateFlow<List<MessageUiState>> = combine(
                            _messages,
                            _searchQuery
                        ) { messages, query ->
                            messages
                                .asSequence()  // Avoid intermediate collections
                                .filter { it.content.contains(query, ignoreCase = true) }
                                .map { it.toUiState() }
                                .toList()
                        }
                        .flowOn(Dispatchers.Default)  // Off main thread
                        .stateIn(
                            scope = viewModelScope,
                            started = SharingStarted.WhileSubscribed(5000),
                            initialValue = emptyList()
                        )
                    }

                    @Composable
                    fun MessageList(viewModel: MessageViewModel) {
                        val filteredMessages by viewModel.filteredMessages.collectAsState()
                        
                        LazyColumn {
                            items(
                                items = filteredMessages,
                                key = { it.id }
                            ) { message ->
                                MessageCard(message)
                            }
                        }
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "What is the primary difference between combine() and zip()?",
                    modifier = Modifier.fontSize(FontSize.Large)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "combine() emits a new value whenever any upstream flow changes, using the latest cached values from all other flows. It is ideal for UI State. zip() waits for matching positional pairs (1st with 1st, 2nd with 2nd) and completes as soon as one flow ends. It is used for Synchronized Pairs.",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            Li(Modifier.padding(top = 1.em).toAttrs()) {
                SpanText(
                    text = "Decision Tree: Which Operator Should You Choose?",
                    modifier = Modifier.fontSize(FontSize.Large)
                        .fontWeight(FontWeight.Bold)
                )

                Ul {
                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Do you need the latest state from multiple sources? → combine()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Do you just want to forward independent events into one stream? → merge()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }

                    Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                        SpanText(
                            text = "Do values need strict 1-to-1 pairing (like a key and its value)? → zip()",
                            modifier = Modifier
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }
        }
    }
}

//https://github.com/droidwithme/product-catalog.git (Best API Calling, Multiple Module, Pagination, Test Cases, Search/Filter with Combine Good)
//https://github.com/safall/Easy-Grocery.git
//https://github.com/felix-leyva/SimpleViewModelExample.git (combine 3 category selection)

/*
   //Combine debounce, distinctUntilChanged, and flatMapLatest
    val results = query
        .debounce(300)                // Wait for typing to pause
        .distinctUntilChanged()       // Ignore same query
        .flatMapLatest { search(it) } // Cancel old search if new query arrives
        .stateIn(viewModelScope, SharingStarted.Lazily, emptyList())
*/

//https://medium.com/@sivavishnu0705/ed5a8a12a644